import { useBusinessDocumentsContext } from '@/components/InvoiceReportDetailDrawer/ApplicationContent/context/BusinessDocumentsContext';
import { usePictureInPictureModeContext } from '@/context/InvoiceReports';
import { usePiPActions } from '@/components/InvoiceReportDetailDrawer/hooks/usePiPActions';
import { useTranslation } from '@/i18n';
import {
  IconDocument,
  IconExpandRight,
  Tabs,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, useCallback, useMemo } from 'react';
import {
  TAB_KEYS,
  usePaymentDetail,
  type TabKey,
} from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import { useFirstInvoiceFileSelection } from '@/components/InvoiceReportDetailDrawer/hooks/useInvoiceFiles';
import { DocumentPreviewTab } from '@/components/InvoiceReportDetailDrawer/RightContent/DocumentPreviewTab';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

const isValidTabKey = (value: string): value is TabKey => {
  const validKeys: readonly string[] = Object.values(TAB_KEYS);
  return validKeys.includes(value);
};

export const RightContent: FC<{ reportId: string }> = ({ reportId }) => {
  const { t } = useTranslation();
  const {
    isRightPaneCollapsed,
    setIsRightPaneCollapsed,
    activeTab,
    setActiveTab,
  } = usePaymentDetail();
  const { processedDocuments, data } = useBusinessDocumentsContext();
  const { isPIPMode } = usePictureInPictureModeContext();
  const { selectFirstInvoiceFile } = useFirstInvoiceFileSelection(data);

  const tabs = useMemo(() => {
    const tabConfig: Array<{
      key: TabKey;
      children: string;
      isDisabled?: boolean;
    }> = [
      {
        key: TAB_KEYS.ATTACHED_INVOICE,
        children: t('attached_invoice'),
        isDisabled: !!isPIPMode,
      },
    ];
    if (processedDocuments?.length > 0) {
      tabConfig.push({
        key: TAB_KEYS.TRANSACTION_DOCUMENTS,
        children: t('business_document'),
      });
    }

    return tabConfig;
  }, [t, processedDocuments, isPIPMode]);

  const {
    handleOpenPictureInPicture,
    handleUpdatePictureInPictureContent,
    pdfViewerContainerRef,
  } = usePiPActions({ tabs });

  const handleTabClick = useCallback(
    (value: React.Key | undefined) => {
      if (value && typeof value === 'string' && isValidTabKey(value)) {
        setActiveTab(value);

        if (value === TAB_KEYS.ATTACHED_INVOICE) {
          selectFirstInvoiceFile();
        }
      }
    },
    [setActiveTab, selectFirstInvoiceFile]
  );

  const isDisabledCollapseToggle = useMemo(() => {
    return !!isPIPMode && tabs.length === 1 && isRightPaneCollapsed;
  }, [isPIPMode, tabs, isRightPaneCollapsed]);

  const handleToggle = useCallback(() => {
    if (isDisabledCollapseToggle) return;
    setIsRightPaneCollapsed(!isRightPaneCollapsed);
  }, [isRightPaneCollapsed, setIsRightPaneCollapsed, isDisabledCollapseToggle]);

  return (
    <div
      className={cx('right-content', {
        collapsed: isRightPaneCollapsed,
      })}
    >
      <div className={cx('collapse-toggle')}>
        <div onClick={handleToggle}>
          <IconExpandRight size={20} />
        </div>
      </div>
      {isRightPaneCollapsed && (
        <div
          onClick={handleToggle}
          className={cx('collapsed-icon-button-container', {
            'disabled-collapse-toggle': isDisabledCollapseToggle,
          })}
        >
          <button
            disabled={isDisabledCollapseToggle}
            type='button'
            className={cx('collapsed-icon-button')}
            aria-label={t('attached_invoice')}
          >
            <span className={cx('collapsed-icon-inner')}>
              <IconDocument size={20} />
            </span>
          </button>
        </div>
      )}
      <div className={cx('pane', { hidden: isRightPaneCollapsed })}>
        <Tabs tabs={tabs} value={activeTab} onClick={handleTabClick} />
        <DocumentPreviewTab
          reportId={reportId}
          pdfViewerContainerRef={pdfViewerContainerRef}
          handleOpenPictureInPicture={handleOpenPictureInPicture}
          handleUpdatePictureInPictureContent={
            handleUpdatePictureInPictureContent
          }
        />
      </div>
    </div>
  );
};
